import {
  EducationalInvestmentCard,
  ContentNuggetBookmark,
  ImageCardProps,
  ImageCard,
  educationalInvestmentTransform,
} from '@backrs_ui/common-ui';

import { EducationalInvestment } from '@backrs_ui/common-ui/src/models/educationalInvestment';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { handleShareWithNavigator } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  setNuggetToAdd,
  openPlaylistListStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Theme, SxProps, Typography, Button } from '@mui/material';

type EducationalInvestmentSectionProps = Readonly<{
  title: string;
  description: string;
  educationalInvestments?: EducationalInvestment[];
  hideCardActions?: boolean;
  tradeOff?: {
    description: string[];
    imageCards?: {
      imageUrl: ImageCardProps['imageUrl'];
      title: ImageCardProps['title'];
      beta?: ImageCardProps['beta'];
      description?: ImageCardProps['description'];
      cta: string;
      onClick?: ImageCardProps['onClick'];
    }[];
  };
}>;

export const EducationalInvestmentSection = ({
  title,
  description,
  educationalInvestments,
  tradeOff,
  hideCardActions = false,
}: EducationalInvestmentSectionProps) => {
  const dispatch = useAppDispatch();
  const { data: bookmarks = [] } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const isEducationalInvestmentBookmarked = (educationalInvestmentId: number) => {
    return (
      bookmarks?.some(
        (b: ContentNuggetBookmark) =>
          b.content_educational_investment?.id === educationalInvestmentId,
      ) || false
    );
  };

  const handleBookmark = async (educationalInvestmentId: number) => {
    const bookmarkId = bookmarks?.find(
      (b: ContentNuggetBookmark) =>
        b.content_educational_investment?.id === educationalInvestmentId,
    )?.id;

    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({
        type: 'educational-investment',
        itemId: educationalInvestmentId,
      });
    }
  };

  const handleAddTask = (educationalInvestment: EducationalInvestment) => {
    const nugget = educationalInvestmentTransform(educationalInvestment);

    dispatch(setNuggetToAdd(nugget));
    dispatch(openPlaylistListStep());
  };

  const handleShare = async (educationalInvestment: EducationalInvestment) => {
    await handleShareWithNavigator({
      title: `Educational Investment: ${educationalInvestment.name}`,
      text: educationalInvestment.description,
      url: `${GROW_URL}${APP_ROUTE.EDUCATIONAL_INVESTMENT}/${educationalInvestment.code.replace(/_/g, '-')}`,
    });
  };

  return (
    <Box>
      <Box sx={titleAndDescriptionContainerSx}>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {title}
        </Typography>
        <Typography variant="text-sm-regular" sx={descriptionSx}>
          {description}
        </Typography>
      </Box>
      {educationalInvestments && educationalInvestments.length > 0 && (
        <Box sx={educationalInvestmentsContainerSx}>
          {educationalInvestments.map((educationalInvestment) => (
            <EducationalInvestmentCard
              key={educationalInvestment.code}
              variant={{
                type: 'LARGE',
                title: educationalInvestment.name,
                description: educationalInvestment.description,
                toSucceedItems: educationalInvestment.toSucceed || [],
                hasBookmarked: isEducationalInvestmentBookmarked(educationalInvestment.id),
                icon: educationalInvestment.icon || 'Activity',
                onAddTask: () => {
                  handleAddTask(educationalInvestment);
                },
                onShare: () => {
                  handleShare(educationalInvestment);
                },
                onBookmark: () => {
                  handleBookmark(educationalInvestment.id);
                },
                hideActions: hideCardActions,
              }}
            />
          ))}
        </Box>
      )}
      {tradeOff && (
        <Box sx={tradeOffContainerSx}>
          <Box sx={tradeOffTextSx}>
            {tradeOff.description.map((text, index) => (
              <Typography
                key={index}
                color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
                variant="text-sm-regular"
              >
                {text}
              </Typography>
            ))}
          </Box>

          <Box sx={tradeOffCardContainerSx}>
            {tradeOff.imageCards?.map((imageCard) => (
              <ImageCard
                key={imageCard.title}
                imageUrl={imageCard.imageUrl}
                title={imageCard.title}
                description={imageCard.description}
                beta={imageCard.beta}
                onClick={imageCard.onClick}
                sxOverrides={{
                  container: imageCardContainerSx(!!imageCard.onClick),
                  image: imageCardImageSx,
                  imageWrapper: imageCardImageWrapperSx,
                  title: imageCardTitleSx,
                  description: imageCardDescriptionSx,
                  titleDescChildContainer: imageCardTitleDescChildContainerSx,
                }}
              >
                <Button
                  id={`educational-investment-image-card-cta-${imageCard.title}`}
                  variant="contained"
                  size="md"
                  hierarchy="Secondary gray"
                  fullWidth
                  onClick={imageCard.onClick}
                  disabled={!imageCard.onClick}
                >
                  <Typography variant="text-sm-semibold">{imageCard.cta}</Typography>
                </Button>
              </ImageCard>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const titleAndDescriptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  pb: backrs.spacing['spacing-3xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const educationalInvestmentsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  width: '100%',
  gap: backrs.spacing['spacing-3xl'],
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
  maxWidth: '100%',
  pt: backrs.spacing['spacing-3xl'],
});

const tradeOffContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-3xl'],
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@container (min-width: 768px)': {
    gridTemplateColumns: '40% 58%',
  },
});

const tradeOffTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const tradeOffCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-3xl'],
  display: 'flex',
  flexDirection: 'column',
  '@container (min-width: 450px)': {
    flexDirection: 'row',
  },
});

const imageCardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  position: 'relative',
  zIndex: 2,
});

const imageCardDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  position: 'relative',
  zIndex: 2,
});

const imageCardContainerSx = (isEnabled: boolean): SxProps<Theme> => ({
  width: '100%',
  maxWidth: { xs: '100%', sm: '100%' },
  position: 'relative',
  '&:hover': {
    boxShadow: 'none',
    transform: 'none',
  },
  cursor: isEnabled ? 'pointer' : 'default',
  '@container (min-width: 450px)': {
    flex: 1,
    minWidth: 0,
    width: 'auto',
    maxWidth: 'none',
    minHeight: '400px',
    height: '100%',
  },
});

const imageCardImageSx: SxProps<Theme> = {
  height: '200px',
  maxHeight: '200px',
  objectFit: 'cover',
};

const imageCardImageWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  height: '200px',
  minHeight: '200px',
  overflow: 'hidden',
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const imageCardTitleDescChildContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};
